import type { ExpectedShape, LLMJudge } from "../llm-judge.js";

/**
 * Shared building blocks for host `LLMJudge` adapters that work by injecting a
 * single-shot "prompt in, string out" call.
 *
 * Every first-party host (Claude Code, Codex, Gemini, Claude Desktop) reaches
 * its LLM differently — a sub-agent tool call, an inline completion, an MCP
 * call, a desktop surface — but that difference is entirely in the *raw
 * invoker* the host injects. The work *around* the call is identical: frame
 * the proposer's prompt so the model returns only the requested shape, then
 * parse the raw response tolerantly. That shared work lives here so each host
 * adapter is a thin shell over one injected function (the design's
 * "subsequent hosts inherit the pattern").
 */

/** A host's single-shot LLM call: a prompt in, a raw string out. */
export interface InjectedInvoker {
  (input: { readonly prompt: string }): Promise<string>;
}

/**
 * Base error for injected-host LLMJudge adapters. Each host subclass narrows
 * the `name` (e.g. `ClaudeCodeLLMJudgeError`) so callers can distinguish the
 * source host while still catching the family with `instanceof LLMJudgeError`.
 */
export class LLMJudgeError extends Error {
  constructor(message: string, readonly raw?: string) {
    super(message);
    this.name = "LLMJudgeError";
  }
}

/**
 * Frame a proposer prompt for a single-shot host LLM call. Host-agnostic —
 * the instruction constrains the response to the expected shape and names no
 * host facility, so every adapter shares it verbatim.
 */
export function frameForJudge(prompt: string, expected: ExpectedShape): string {
  const tail = expected.shape === "json"
    ? "\n\nReturn ONLY the JSON object described above. No prose, no markdown code fences, no explanation around it. If you cannot answer, return the JSON with verdict=\"no\" (for capture decisions) or actionKind=\"create-file\" with a brief rationale (for placement decisions)."
    : "\n\nReturn ONLY the answer string. No prose preamble, no markdown formatting around it.";
  return prompt + tail;
}

/**
 * Parse a raw host response into the expected shape. Strings pass through
 * trimmed; JSON answers are extracted tolerantly (a leading/trailing ```json
 * fence, or a sentence wrapped around the object) and `JSON.parse`'d.
 * Persistent format failure throws via the injected `makeError` factory so
 * each adapter raises its own typed error rather than the base masking it.
 */
export function parseJudgeResponse(
  raw: string,
  expected: ExpectedShape,
  makeError: (message: string, raw?: string) => Error,
): unknown {
  if (typeof raw !== "string") {
    throw makeError("Host LLM returned a non-string response.");
  }
  const stripped = raw.trim();
  if (expected.shape === "string") {
    return stripped;
  }
  const jsonPayload = extractJsonPayload(stripped);
  try {
    return JSON.parse(jsonPayload);
  } catch (e) {
    throw makeError(`Host LLM response was not valid JSON: ${(e as Error).message}`, raw);
  }
}

function extractJsonPayload(s: string): string {
  // Strip leading/trailing markdown code fences if present.
  const fenced = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fenced) return fenced[1]!.trim();
  // Find the first { and matching last } — tolerant of a leading sentence.
  const firstBrace = s.indexOf("{");
  const lastBrace = s.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    return s.slice(firstBrace, lastBrace + 1);
  }
  return s;
}

/**
 * Shared base for host adapters built on an injected invoker. The base owns
 * framing + parsing; a subclass declares only the host id and (optionally)
 * overrides {@link makeError} to raise a host-named error type.
 */
export abstract class InjectedLLMJudge implements LLMJudge {
  abstract readonly host: string;

  constructor(protected readonly invoker: InjectedInvoker) {}

  /** Host adapters override to raise a host-named error subclass. */
  protected makeError(message: string, raw?: string): Error {
    return new LLMJudgeError(message, raw);
  }

  async ask(prompt: string, expected: ExpectedShape): Promise<unknown> {
    const framed = frameForJudge(prompt, expected);
    const raw = await this.invoker({ prompt: framed });
    return parseJudgeResponse(raw, expected, (m, r) => this.makeError(m, r));
  }
}
